import Chart from './Chart'

import { select } from 'd3-selection'
import {
  scaleLinear,
  scaleOrdinal,
} from 'd3-scale'
import {
  range,
  merge
} from 'd3-array'
import { transition } from 'd3-transition'
import { easeCubic } from 'd3-ease'
import { line } from 'd3-shape'
import {
  axisLeft,
  axisRight,
} from 'd3-axis'


const d3 = {
  select,
  scaleLinear,
  merge,
  transition,
  easeCubic,
  line,
  axisLeft,
  axisRight,
  scaleOrdinal,
  range,
}

export default class EventsChart extends Chart {

  constructor(settings, data, id) {
    super(settings, data, id)

    this.settings = Object.assign({
      xScale: d3.scaleLinear(),
      strokeWidth: 1.5,
      animate: true,
    }, this.settings)

    return this
  }

  setDimensions( svg ) {

    let width = svg.node().getBoundingClientRect().width

    width = Math.min(width, this.settings.width)

    let needsRedraw = this.forceRedraw || (this.width != width)
    this.forceRedraw = false

    this.width = width
    this.innerWidth = width - this.padding.left - this.padding.right

    return needsRedraw

  }

  draw() {

    let settings = this.settings

    let svg = d3.select( '#' + this.id )

    if( !this.setDimensions( svg ) ) return

    this.setPanicScales()
    this.setBalanceSheetScales()

    let container = svg
      .selectAll('g.chart-container').data([ this.data ])

    let containerEnter = container.enter().append('g')
      .attr('class', 'chart-container')

    let t = d3.transition()
      .duration( this.settings.animate ? 750 : 0 )
      .ease(d3.easeCubic)

    let lineClip = containerEnter.append('clipPath')
      .attr('id', 'lineClip')
      .append('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('height', this.innerHeight)
        .attr('width', '0%')

    this.animate = function() {
      let a = d3.transition()
        .duration( this.settings.animate ? 10000 : 0 )
        .ease(d3.easeCubic)

      container.merge( containerEnter ).select('#lineClip rect')
        .transition(a)
          .attr('width', (100 * this.innerWidth/this.width) + '%')
          .on('end', function() {
            d3.select(this).attr('width', '100%')
          })
    }

    let clipPath = container.merge( containerEnter )
      .select('#lineClip').select('rect')

    let triggersGroup = container.merge( containerEnter ).selectAll('g.trigger')
        .data( this.data.triggerData )

    let triggersGroupEnter = triggersGroup.enter().append('g')
        .attr('class', 'trigger')

    triggersGroup.merge( triggersGroupEnter )
        .attr('transform', (d) => `translate(${ this.padding.left + this.xScale( d.date ) },${ this.padding.top })`)

    let triggerLines = triggersGroup.merge( triggersGroupEnter ).selectAll('line.trigger-line')
        .data((d) => [ d ])

    let triggerLinesEnter = triggerLines.enter().append('line')
        .attr('class', 'trigger-line')
        .attr('x1', 0)
        .attr('x2', 0)
        .attr('y1', this.innerHeight)
        .attr('y2', -10)
        .style('stroke', 'rgb(24, 61, 108)')
        .style('opacity', 0.2)

    let triggerText = triggersGroup.merge( triggersGroupEnter ).selectAll('text.trigger-text')
        .data((d) => [ d ])

    let triggerTextEnter = triggerText.enter().append('text')
        .attr('class', 'trigger-text')


    if(this.width > 727) {

      triggerText.merge( triggerTextEnter )
          .attr('x', 0)
          .attr('y', 0)
          .attr('text-anchor', 'middle')
          .attr('font-size', 10)
          .style('fill', 'rgb(24, 61, 108)')
          .style('opacity', 0.5)
          .attr('transform', 'rotate(0)')
          .attr('y', (d) => d.title.split(' ').length > 2 ? -26 : -14)
          .text((d) => d.title)

      triggerLines.merge( triggerLinesEnter )
          .attr('y2', (d) => d.title.split(' ').length > 2 ? -23 : -10)

    } else {

      triggerText.merge( triggerTextEnter )
          .attr('class', 'trigger-text')
          .attr('x', -10)
          .attr('y', -2)
          .attr('text-anchor', 'start')
          .attr('font-size', 10)
          .style('fill', 'rgb(24, 61, 108)')
          .style('opacity', 0.5)
          .attr('transform', 'rotate(90)')
        .text((d) => d.title)

      triggerLines.merge( triggerLinesEnter )
          .attr('y2', -10)

    }

    let linesGroupPanic = container.merge( containerEnter ).selectAll('g.panic-container')
        .data( this.data.panicData )

    let linesGroupPanicEnter = linesGroupPanic.enter().append('g')
        .attr('class', (d,i) => 'panic-container' )
        .attr('transform', `translate( ${ this.padding.left }, ${ this.padding.top } )`)

    let line = d3.line()
      .x((d) => this.xScale( d.x ))
      .y((d) => this.yScalePanic( +d.y ))

    let linesP = linesGroupPanicEnter.append('path')
        .style('fill', 'none')
        .style('stroke-width', this.settings.strokeWidth)
        .attr('clip-path', 'url(#lineClip)')

    let _this = this

    linesGroupPanic.merge(linesGroupPanicEnter).each(function(d,i) {
      let path = d3.select(this).select('path').attr('d', line(d.values) )

      path
          .style('stroke', _this.settings.colors( d.title ))
          .attr('class', 'path line-' + i )

    })

    let linesGroupBalanceSheet = container.merge( containerEnter ).selectAll('g.line-balance-sheet')
        .data( this.data.balanceSheetData )

    let linesGroupBalanceSheetEnter = linesGroupBalanceSheet.enter().append('g')
        .attr('class', (d,i) => 'lines line-balance-sheet' )
        .attr('transform', `translate( ${ this.padding.left }, ${ this.padding.top } )`)

    let lineBS = d3.line()
      .x((d) => this.xScale( d.x ))
      .y((d) => this.yScaleBalanceSheet( +d.y ))

    let linesBS = linesGroupBalanceSheetEnter.append('path')
        .style('fill', 'none')
        .style('stroke-width', this.settings.strokeWidth)
        .attr('clip-path', 'url(#lineClip)')

    linesGroupBalanceSheet.merge( linesGroupBalanceSheetEnter ).each(function(d,i) {
      let path = d3.select(this).select('path').attr('d', lineBS(d.values) )

      d3.select(this).select('path')
          .style('stroke', _this.settings.colors( d.title ))
          .attr('class', 'path line-' + i )

    })

    this.drawXAxis(container.merge( containerEnter ))

    this.drawYAxisP(container.merge( containerEnter ), this.yScalePanic )
    this.drawYAxisBS(container.merge( containerEnter ), this.yScaleBalanceSheet )

    this.drawLegend(container.merge( containerEnter ))

  }

  setPanicScales() {

    this.xScale = this.settings.xScale
      .rangeRound([ 0, this.innerWidth ])

    let allData = this.data.panicData
    allData = allData.map((d) => d.values)

    let yMax = Math.max.apply( Math, d3.merge( allData ).map((d) => d.y) )

    let yMin = Math.min.apply( Math, d3.merge( allData ).map((d) => d.y) )

    this.yScalePanic = d3.scaleLinear()
      .domain([ Math.min( 0, yMin ), Math.max( 0, yMax ) ])
      .rangeRound([ this.innerHeight, 0 ])

  }

  setBalanceSheetScales() {

    let allData = this.data.balanceSheetData
    allData = allData.map((d) => d.values)

    let yMax = Math.max.apply( Math, d3.merge( allData ).map((d) => d.y) )

    let yMin = Math.min.apply( Math, d3.merge( allData ).map((d) => d.y) )

    this.yScaleBalanceSheet = d3.scaleLinear()
      .domain([ Math.min( 0, yMin ), Math.max( 0, yMax ) ])
      .rangeRound([ this.innerHeight, 0 ])

  }

  drawYAxisP(container, yScale, balanceSheet) {

    if(!this.settings.showAxes) return

    let _this = this

    let yAxis = d3.axisLeft( yScale )
      .tickFormat( this.settings.yTickFormat )
      .ticks( this.settings.yTicks )

    let axisContainer = container
        .selectAll('g.y-axis-panic').data((d) => [ d ])

    let axisContainerEnter = axisContainer.enter().append('g')
        .attr('class', 'axis y-axis-panic')

    axisContainer.merge( axisContainerEnter )
      .attr('transform', `translate(${ this.padding.left - this.settings.yAxisPadding }, ${ this.padding.top })`)
      .call( yAxis )

    container.select('.y-axis-panic')
      .selectAll('.tick').selectAll('line')
      .each(function() {
        d3.select(this).style('stroke', '#1a1a1a')
      })

    container.select('.y-axis-panic')
      .selectAll('path')
      .each(function() {
        d3.select(this).remove()
      })

    let yAxisLabel = container
        .select('.y-axis-panic').selectAll('text.axis-label')
      .data([ this.settings.yAxis1Label ])

    let yAxisLabelEnter = yAxisLabel
      .enter().append('text')
        .attr('class', 'axis-label')
        .text((d) => d)
        .attr('y', -this.settings.padding.left + 20)
        .attr('text-anchor', 'middle')
        .attr('transform', 'rotate(-90)')
        .style('fill', '#1a1a1a')
        .attr('font-size', 12)

    yAxisLabel.merge( yAxisLabelEnter )
        .attr('x', -this.innerHeight/2)

    let factorTicks = container.select('.y-axis-panic').selectAll('line.factor-line')
      .data( this.data.panicData.map((d) => ({
          key: d.title,
          value: d.values[0].y
        }))
      )

    let factorTicksEnter = factorTicks.enter().append('line')
      .attr('class', '.factor-line')
      .attr('x1', -6)
      .attr('x2', 0)
      .attr('y1', (d) => yScale(d.value))
      .attr('y2', (d) => yScale(d.value))
      .style('stroke', (d) => this.settings.colors(d.key))
      .style('stroke-width', 2)


  }

  drawYAxisBS(container, yScale, balanceSheet) {

    if(!this.settings.showAxes) return

    let _this = this

    let tickScale = d3.scaleOrdinal()
      .domain([ 0, 25, 50, 75, 100 ])
      .range(d3.range( this.innerHeight, 0, -this.innerHeight/4.001)) //4.001 so it doesn't overlap

    let yAxis = d3.axisRight( tickScale )
      .tickFormat( this.settings.yTickFormat )
      .ticks( this.settings.yTicks )

    let axisContainer = container
        .selectAll('g.y-axis-balance').data((d) => [ d ])

    let axisContainerEnter = axisContainer.enter().append('g')
        .attr('class', 'axis y-axis-balance')

    axisContainer.merge( axisContainerEnter )
      .attr('transform', `translate(${ this.padding.left + this.innerWidth + this.settings.yAxisPadding }, ${ this.padding.top })`)
      .call( yAxis )

    container.select('.y-axis-balance')
      .selectAll('.tick').selectAll('line')
      .each(function() {
          d3.select(this).style('stroke', '#1a1a1a')
      })

    container.select('.y-axis-balance')
      .selectAll('path')
      .each(function() {
        d3.select(this).remove()
      })

    let yAxisLabel = container
        .select('.y-axis-balance').selectAll('text.axis-label')
      .data([ this.settings.yAxis2Label ])

    let yAxisLabelEnter = yAxisLabel
      .enter().append('text')
        .attr('class', 'axis-label')
        .text((d) => d)
        .attr('y', -this.padding.right - this.settings.yAxisPadding + 35 )
        .attr('text-anchor', 'middle')
        .attr('transform', 'rotate(90)')
        .style('fill', '#1a1a1a')
        .attr('font-size', 12)

    yAxisLabel.merge( yAxisLabelEnter )
        .attr('x', this.innerHeight/2)

    let factorTicks = container.select('.y-axis-balance').selectAll('line.factor-line')
      .data( this.data.balanceSheetData.map((d) => ({
          key: d.title,
          value: d.values[ d.values.length-1 ].y
        }))
      )

    let factorTicksEnter = factorTicks.enter().append('line')
      .attr('class', '.factor-line')
      .attr('x1', 0)
      .attr('x2', 6)
      .attr('y1', (d) => yScale(d.value))
      .attr('y2', (d) => yScale(d.value))
      .style('stroke', (d) => this.settings.colors(d.key))
      .style('stroke-width', 2)


  }

  drawLegend(container) {

    let legendContainer = container
        .selectAll('g.legend').data([ this.data.panicData.concat(this.data.balanceSheetData) ])

    let legendContainerEnter = legendContainer.enter().append('g')
        .attr('class', 'legend')
        .attr('font-size', 12)
        .attr('transform', 'translate(0,1)')
        .attr('text-anchor', 'start')

    let legendItems = legendContainer.merge( legendContainerEnter )
        .selectAll('g.legend-item').data( this.settings.colors.domain() )

    let legendItemsEnter = legendItems.enter().append('g')
        .attr('class', 'legend-item')

    let rect = legendItemsEnter.append('rect')
        // .attr('x', 0)
        // .attr('y', 0)
        .attr('width', 12)
        .attr('height', 12)
        .style('fill', (d,i) => this.settings.colors( d ))

    let legendText = legendItemsEnter.append('text')
        .attr('x', 16)
        .attr('y', 6)
        .attr('dy', '0.32em')
        .style('fill', '#1a1a1a')
        .text((d) => d)

    legendText
        .append('tspan')
        .text((d,i,j) => j.length - i)
        .attr('font-size', 8)
        .attr('dy', -4)
        .attr('dx', 1)

    let _this = this,
      previousLeft = 0,
      top = 0.5

    legendItems.merge( legendItemsEnter )
      .each(function(d,i) {

        let leftPadding = 8 * ( i ? 1 : 0 )

        previousLeft = this.getBoundingClientRect().width + previousLeft + leftPadding

        if(previousLeft > _this.innerWidth) {
          top += 18
          leftPadding = 0
          previousLeft = this.getBoundingClientRect().width + leftPadding
        }

        d3.select(this)
          .attr('transform', `translate(${ _this.innerWidth + _this.padding.left - previousLeft },${ top })`)
      })


  }

  setColors(colors) {
    this.settings.colors = colors
  }

}
